import { Flex } from 'theme-ui';
import React, { useEffect, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { toast } from 'react-toastify';
import {
    useAddDiscountAgreementsToSubscriptionMutation,
    useGetCompanyOwnersQuery,
    useGetCompaniesQuery,
} from '@/store/apiService';
import { GrayFormikField } from '@/components/GrayFormikField';
import Button from '@/components/Button';
import { getErrorMessage } from '@/utils/helpers';

interface SelectCompanyFormProps extends FormikProps<FormValues> {}

const SelectCompanyForm: React.FC<SelectCompanyFormProps> = (props) => {
    const [companySearch, setCompanySearch] = useState('');

    const companies = useGetCompaniesQuery({ Take: 20, Search: companySearch });
    const companyOwners = useGetCompanyOwnersQuery({ IncludeCompanyOwner: true });

    useEffect(() => {
        if (!props.values.companyId) {
            props.setFieldValue('companyOwnerId', '');
        }
    }, [props.values.companyId]);

    return (
        <Flex sx={{ gap: 3, flexDirection: 'column' }}>
            <GrayFormikField
                name="companyId"
                value={props.values.companyId}
                type="dropdownsearch"
                label="Company"
                placeholder="Search companies"
                onSearchChange={(val) => {
                    setCompanySearch(val);
                }}
                onSelect={(item) => {
                    props.setFieldValue('companyId', item?.value);
                }}
                items={companies.data?.Results?.map((company) => ({
                    value: company.Id,
                    label: company.Name,
                }))}
                isLoading={companies.isFetching}
                total={companies.data?.Total}
            />

            <GrayFormikField
                type="select"
                name="companyOwnerId"
                label="Company owner id"
                placeholder="Select company owner"
                options={companyOwners?.data?.Results?.map((company) => ({
                    value: company.Id,
                    label: company.Name,
                }))}
                value={
                    companies.data?.Results?.find((c) => c.Id === props.values.companyId)
                        ?.CompanyOwnerId || props.values.companyOwnerId
                }
                onChange={(evt: any) => {
                    props.setFieldValue('companyOwnerId', evt.target.value);
                }}
                disabled={companyOwners.isFetching || !props.values.companyId}
                isLoading={companyOwners.isFetching}
            />

            <Flex
                p="2"
                sx={{
                    width: '100%',
                    gap: 2,
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    isLoading={props.isSubmitting}
                    disabled={props.isSubmitting}
                    type="submit"
                    onClick={props.submitForm}
                >
                    Conect
                </Button>
                {/* TODO: add errors */}
            </Flex>
        </Flex>
    );
};

interface SelectCompanyProps {
    discountAgreements: { DiscountAgreementId: string }[];
    onSubmitSuccess?: () => void;
}

export interface FormValues {
    companyText: string;
    companyId: string;
    companyOwnerId: string;
}

const ConnectDiscountAgreement: React.FC<SelectCompanyProps> = ({
    discountAgreements,
    onSubmitSuccess = () => {},
}) => {
    const [connectDiscountAgreement, connectDiscountAgreementState] =
        useAddDiscountAgreementsToSubscriptionMutation();

    return (
        <Formik<FormValues>
            initialValues={{
                companyText: '',
                companyId: '',
                companyOwnerId: '',
            }}
            onSubmit={(values, { resetForm, setErrors, setSubmitting }) => {
                return connectDiscountAgreement({
                    CompanyOwnerId: values.companyOwnerId,
                    CompanyId: values.companyId,
                    DiscountAgreements: discountAgreements,
                })
                    .unwrap()
                    .then((res) => {
                        onSubmitSuccess();
                        toast.success('Successfully connected');
                    })
                    .catch((err) => {
                        setSubmitting(false);
                        toast.error(getErrorMessage(err), { autoClose: 5000 });
                    });
            }}
        >
            {(props) => <SelectCompanyForm {...props} />}
        </Formik>
    );
};

export default ConnectDiscountAgreement;
